import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  ModalDialog,
  Sheet,
  Stack,
  Table,
  Typography,
} from '@mui/joy';
import { FaEye, FaSyncAlt } from 'react-icons/fa';
import { directSupabase } from '../lib/supabaseClient';
import { IconWrapper } from '../components/IconWrapper';

type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'rejected';

type LeadRow = {
  id: string;
  status: LeadStatus;
  source: string;
  transport: string | null;
  meta_lead_id: string | null;
  created_time: string | null;
  full_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  postcode: string | null;
  form_id: string | null;
  form_name: string | null;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  platform: string | null;
  custom_answers: unknown;
  raw_payload: unknown;
  converted_at: string | null;
  customer_id: string | null;
  project_id: string | null;
  created_at: string;
  updated_at: string;
};

const STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  qualified: 'Qualified',
  converted: 'Converted',
  rejected: 'Rejected',
};

const STATUS_COLORS: Record<LeadStatus, 'primary' | 'warning' | 'success' | 'danger' | 'neutral'> = {
  new: 'primary',
  contacted: 'warning',
  qualified: 'success',
  converted: 'success',
  rejected: 'neutral',
};

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-GB');
}

function displayValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) return value.map(displayValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function humanizeKey(value: string) {
  return value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function objectEntries(value: unknown): Array<[string, unknown]> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.entries(value as Record<string, unknown>);
}

const LeadsPage: React.FC = () => {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLead, setSelectedLead] = useState<LeadRow | null>(null);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const pageSize = 1000;
      const allRows: LeadRow[] = [];
      let page = 0;

      while (true) {
        const { data, error: loadError } = await (directSupabase as any)
          .from('leads')
          .select('*')
          .order('created_at', { ascending: false })
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (loadError) throw loadError;

        const rows = (data || []) as LeadRow[];
        allRows.push(...rows);
        if (rows.length < pageSize) break;
        page += 1;
      }

      setLeads(allRows);
    } catch (loadError: any) {
      setError(loadError?.message || 'Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();

    const channel = directSupabase
      .channel('jarvis-leads-live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'leads' }, () => loadLeads())
      .subscribe();

    return () => {
      directSupabase.removeChannel(channel);
    };
  }, [loadLeads]);

  const metrics = useMemo(
    () => ({
      total: leads.length,
      new: leads.filter((lead) => lead.status === 'new').length,
      qualified: leads.filter((lead) => lead.status === 'qualified').length,
      converted: leads.filter((lead) => lead.status === 'converted').length,
    }),
    [leads]
  );

  const selectedAnswers = objectEntries(selectedLead?.custom_answers);

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'center' }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography level="h2">Leads</Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Enquiries received from Meta, Facebook, Instagram and future lead sources.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startDecorator={<IconWrapper icon={FaSyncAlt} />}
          onClick={loadLeads}
          loading={loading}
        >
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: metrics.total, color: 'neutral' as const },
          { label: 'New', value: metrics.new, color: 'primary' as const },
          { label: 'Qualified', value: metrics.qualified, color: 'success' as const },
          { label: 'Converted', value: metrics.converted, color: 'success' as const },
        ].map((metric) => (
          <Card key={metric.label} variant="soft" color={metric.color} sx={{ flex: 1 }}>
            <Typography level="title-sm">{metric.label}</Typography>
            <Typography level="h2">{metric.value}</Typography>
          </Card>
        ))}
      </Stack>

      <Card variant="outlined">
        {loading && leads.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
            <CircularProgress />
            <Typography level="body-sm">Loading leads…</Typography>
          </Stack>
        ) : leads.length === 0 ? (
          <Alert color="neutral">No leads found in the database.</Alert>
        ) : (
          <Sheet variant="plain" sx={{ overflowX: 'auto' }}>
            <Table hoverRow stickyHeader sx={{ minWidth: 980 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Source</th>
                  <th>Campaign / form</th>
                  <th>Status</th>
                  <th>Received</th>
                  <th style={{ width: 90 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id}>
                    <td>
                      <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                        {lead.full_name || 'Unnamed lead'}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {lead.postcode || 'No postcode'}
                      </Typography>
                    </td>
                    <td>
                      <Typography level="body-sm">{lead.email || '—'}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{lead.phone || '—'}</Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft">{humanizeKey(lead.source)}</Chip>
                    </td>
                    <td>
                      <Typography level="body-sm">{lead.campaign_name || '—'}</Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {lead.form_name || lead.form_id || 'No form'}
                      </Typography>
                    </td>
                    <td>
                      <Chip size="sm" variant="soft" color={STATUS_COLORS[lead.status] || 'neutral'}>
                        {STATUS_LABELS[lead.status] || humanizeKey(lead.status)}
                      </Chip>
                    </td>
                    <td>{formatDateTime(lead.created_time || lead.created_at)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outlined"
                        startDecorator={<IconWrapper icon={FaEye} />}
                        onClick={() => setSelectedLead(lead)}
                      >
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        )}
      </Card>

      <Modal open={!!selectedLead} onClose={() => setSelectedLead(null)}>
        <ModalDialog sx={{ width: { xs: '96vw', md: '78vw' }, maxWidth: 1050, maxHeight: '92vh' }}>
          <DialogTitle>Lead details</DialogTitle>
          <DialogContent sx={{ overflow: 'auto' }}>
            {selectedLead && (
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <Typography level="title-md" sx={{ mb: 1 }}>Contact</Typography>
                    <Stack spacing={0.75}>
                      <Typography><b>Name:</b> {selectedLead.full_name || '—'}</Typography>
                      <Typography><b>Email:</b> {selectedLead.email || '—'}</Typography>
                      <Typography><b>Phone:</b> {selectedLead.phone || '—'}</Typography>
                      <Typography><b>Postcode:</b> {selectedLead.postcode || '—'}</Typography>
                      <Typography><b>Received:</b> {formatDateTime(selectedLead.created_time || selectedLead.created_at)}</Typography>
                    </Stack>
                  </Card>

                  <Card variant="outlined" sx={{ flex: 1 }}>
                    <Typography level="title-md" sx={{ mb: 1 }}>Attribution</Typography>
                    <Stack spacing={0.75}>
                      <Typography><b>Source:</b> {humanizeKey(selectedLead.source)}</Typography>
                      <Typography><b>Transport:</b> {humanizeKey(selectedLead.transport || '—')}</Typography>
                      <Typography><b>Campaign:</b> {selectedLead.campaign_name || selectedLead.campaign_id || '—'}</Typography>
                      <Typography><b>Ad set:</b> {selectedLead.adset_name || selectedLead.adset_id || '—'}</Typography>
                      <Typography><b>Ad:</b> {selectedLead.ad_name || selectedLead.ad_id || '—'}</Typography>
                      <Typography><b>Form:</b> {selectedLead.form_name || selectedLead.form_id || '—'}</Typography>
                      <Typography><b>Meta Lead ID:</b> {selectedLead.meta_lead_id || '—'}</Typography>
                    </Stack>
                  </Card>
                </Stack>

                <Card variant="outlined">
                  <Typography level="title-md" sx={{ mb: 1 }}>Custom form answers</Typography>
                  {selectedAnswers.length === 0 ? (
                    <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>No custom answers supplied.</Typography>
                  ) : (
                    <Table>
                      <tbody>
                        {selectedAnswers.map(([key, value]) => (
                          <tr key={key}>
                            <td style={{ width: '38%' }}>
                              <Typography level="body-sm" sx={{ fontWeight: 700 }}>{humanizeKey(key)}</Typography>
                            </td>
                            <td>{displayValue(value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card>

                <Card variant="outlined">
                  <Typography level="title-md" sx={{ mb: 1 }}>Raw payload</Typography>
                  <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', overflow: 'auto', maxHeight: 300 }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 12 }}>
                      {JSON.stringify(selectedLead.raw_payload || {}, null, 2)}
                    </pre>
                  </Sheet>
                </Card>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setSelectedLead(null)}>Close</Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default LeadsPage;
